import { useMemo } from 'react';

import { getEmailFromClaim, getIsInternalUser, getUserInfoFromClaim } from 'api/authentication/store/selectors';
import { useAccessTokenStore } from 'api/authentication/store/useAccessTokenStore';

import { GRID_STORE } from '../grids/gridState';
import { APP_STATE, EConsoleMessageSeverity } from './appState';

export const useIsDraggingColumn = () => {
  const draggingColumn = APP_STATE((state) => state.isDraggingColumn);

  return draggingColumn;
};

export const useIsDraggingColumnAtIndex = (columnIndex: number) => {
  const draggingColumnIndex = APP_STATE((state) => state.draggingColumnIndex);

  return !!draggingColumnIndex && draggingColumnIndex === columnIndex;
};

export const useIsCellHovered = (resultSetId: string, columnIndex: number, rowIndex: number) => {
  return APP_STATE((state) => {
    return (
      state.hoveredCellResultSetId === resultSetId &&
      state.hoveredCellColumnIndex === columnIndex &&
      state.hoveredCellRowIndex === rowIndex
    );
  });
};

export const useIsSpaceBarDown = () => {
  return APP_STATE((state) => state.spaceBarDown);
};

export const useIsCopyRequested = (resultSetEntry: string, columnIndex: number, rowIndex: number) => {
  const copyRequested = GRID_STORE((state) => state.copyRequested[resultSetEntry]);

  return useMemo(() => {
    if (copyRequested !== undefined) {
      if (copyRequested[columnIndex] !== undefined) {
        return copyRequested[columnIndex] === rowIndex;
      }
    }
    return false;
  }, [copyRequested, columnIndex, rowIndex]);
};

export const useIsPasteRequested = (resultSetEntry: string, columnIndex: number, rowIndex: number) => {
  return GRID_STORE((state) => state.pasteRequested[resultSetEntry]?.[columnIndex] === rowIndex);
};

export const useIsDeleteDown = () => {
  const isShiftDown = APP_STATE((state) => state.deleteDown);

  return isShiftDown;
};

export const useLogs = () => {
  return useMemo(() => {
    return {
      enableLogs: () => {
        APP_STATE.getState().enableAppConsoleLogs(true);
      },

      disableLogs: () => {
        APP_STATE.getState().enableAppConsoleLogs(false);
      },

      logDebug: (message: string) => {
        APP_STATE.getState().log({ message: message, severity: EConsoleMessageSeverity.Debug, date: new Date() });
      },
      logTrace: (message: string) => {
        APP_STATE.getState().log({ message: message, severity: EConsoleMessageSeverity.Trace, date: new Date() });
      },
      logWarning: (message: string) => {
        APP_STATE.getState().log({ message: message, severity: EConsoleMessageSeverity.Warning, date: new Date() });
      },
      logCritical: (message: string) => {
        APP_STATE.getState().log({ message: message, severity: EConsoleMessageSeverity.Critical, date: new Date() });
      }
    };
  }, []);
};

export const useSubscriptionTechnicalName = () => {
  const database = useAccessTokenStore((state) => state.accessTokenPayload?.database);

  if (!database) {
    throw new Error('Database not found in access token');
  }

  return database;
};

export interface UserInfo {
  sub: string;

  firstName: string;
  lastName: string;

  fullName: string;

  username: string;
  initials: string;

  tooltip?: string;
}

export const useUserEmail = () => {
  return useAccessTokenStore(getEmailFromClaim);
};

export const useUserWorksAtLevvr = () => {
  return useAccessTokenStore(getIsInternalUser);
};

export const useUserInfo = (): UserInfo => {
  return useAccessTokenStore(getUserInfoFromClaim);
};
